import { useState, useCallback, useEffect, useRef } from 'react';
import { Upload, Loader2, Trash2, ArrowLeft, Music2, AlertTriangle, Type } from 'lucide-react';
import { templatesApi, getAuthToken } from '@/services/api';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';

const BASE = process.env.REACT_APP_BACKEND_URL;
const img = (url) => `${BASE}${url}?auth=${getAuthToken()}`;
const fmt = (s) => `${s.toFixed(2)}s`;

const StatusBadge = ({ t }) => {
  const map = {
    ready: 'bg-teal-400/15 text-teal-300 border-teal-500/30',
    processing: 'bg-amber-400/15 text-amber-300 border-amber-500/30',
    queued: 'bg-zinc-700/40 text-zinc-300 border-zinc-600',
    error: 'bg-red-500/15 text-red-300 border-red-500/30',
  };
  const label = t.status === 'processing' && t.progress
    ? `procesando ${t.progress.done}/${t.progress.total}`
    : t.status;
  return (
    <span className={`rounded-full border px-2.5 py-0.5 text-xs ${map[t.status] || map.queued}`}
          data-testid={`template-status-${t.id}`}>{label}</span>
  );
};

export default function Templates() {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [form, setForm] = useState({ name: '', source_creator: '', source_music_name: '', niches: '' });
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    try { setList(await templatesApi.list()); } catch (e) { setError(e.message); }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!list.some((t) => t.status === 'queued' || t.status === 'processing')) return;
    const id = setInterval(load, 4000);
    return () => clearInterval(id);
  }, [list, load]);

  const openTemplate = useCallback(async (id) => {
    setError(null);
    try { setOpen(await templatesApi.get(id)); } catch (e) { setError(e.message); }
  }, []);

  const submit = useCallback(async (e) => {
    e.preventDefault();
    const file = fileRef.current?.files?.[0];
    if (!file) { setError('Selecciona un fichero de vídeo'); return; }
    setUploading(true); setError(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      await templatesApi.import(fd);
      fileRef.current.value = '';
      setForm({ name: '', source_creator: '', source_music_name: '', niches: '' });
      await load();
    } catch (err) { setError(err.message); } finally { setUploading(false); }
  }, [form, load]);

  const remove = useCallback(async (id) => {
    try { await templatesApi.delete(id); setOpen(null); await load(); }
    catch (e) { setError(e.message); }
  }, [load]);

  if (open) {
    const total = (open.scenes || []).reduce((a, s) => a + s.duration, 0);
    return (
      <div className="min-h-screen bg-[#0a0a0b] px-4 py-10 text-zinc-100 sm:px-6 lg:px-16" data-testid="template-detail">
        <Button variant="ghost" onClick={() => setOpen(null)} className="mb-6 gap-2" data-testid="back-to-templates">
          <ArrowLeft className="h-4 w-4" /> Plantillas
        </Button>
        <h1 className="text-4xl font-semibold sm:text-5xl" data-testid="template-name">{open.name}</h1>
        <div className="mt-4 flex flex-wrap gap-x-6 gap-y-2 text-sm text-zinc-400">
          <span data-testid="template-meta-scenes">{open.scene_count} escenas</span>
          <span data-testid="template-meta-duration">{open.duration?.toFixed(2)}s de vídeo</span>
          <span>suma de escenas {total.toFixed(2)}s</span>
          {open.source_creator && <span>creador @{open.source_creator}</span>}
          {open.source_music_name && (
            <span className="flex items-center gap-1.5 text-teal-300" data-testid="template-music">
              <Music2 className="h-3.5 w-3.5" /> {open.source_music_name}
            </span>
          )}
        </div>

        <h2 className="mt-12 text-base font-medium md:text-lg">Escenas</h2>
        <div className="mt-5 flex gap-4 overflow-x-auto pb-4" data-testid="template-scenes-row">
          {(open.scenes || []).map((s) => (
            <article key={s.index} className="w-56 shrink-0 overflow-hidden rounded-xl border border-zinc-800 bg-zinc-900/50"
                     data-testid={`template-scene-${s.index}`}>
              <div className="relative aspect-[9/16] bg-black">
                <img src={img(s.keyframe_url)} alt={s.description} loading="lazy" className="h-full w-full object-cover" />
                <span className="absolute left-2 top-2 rounded-full bg-black/75 px-2 py-0.5 text-xs">#{s.index}</span>
                <span className="absolute right-2 top-2 rounded-full bg-black/75 px-2 py-0.5 text-xs">{fmt(s.duration)}</span>
                {s.raw_text && (
                  <span className="absolute bottom-2 left-2 right-2 flex items-start gap-1 rounded bg-teal-500/90 px-2 py-1 text-xs font-medium text-black">
                    <Type className="mt-0.5 h-3 w-3 shrink-0" />
                    <span data-testid={`template-scene-text-${s.index}`}>{s.raw_text}</span>
                  </span>
                )}
              </div>
              <p className="p-3 text-xs leading-relaxed text-zinc-300" data-testid={`template-scene-desc-${s.index}`}>
                {s.description}
              </p>
            </article>
          ))}
        </div>

        <h2 className="mt-12 text-base font-medium md:text-lg">Captions ({(open.captions || []).length})</h2>
        {open.caption_warning && (
          <div className="mt-4 flex max-w-3xl items-start gap-2 rounded-lg border border-amber-700/60 bg-amber-950/30 px-4 py-3 text-sm text-amber-200"
               data-testid="template-caption-warning">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{open.caption_warning_message}</span>
          </div>
        )}
        <div className="mt-5 max-w-3xl space-y-3" data-testid="template-captions">
          {(open.captions || []).length === 0 && (
            <p className="text-sm text-zinc-500" data-testid="template-no-captions">
              No se detectó texto sobreimpreso en este reel.
            </p>
          )}
          {(open.captions || []).map((c, i) => (
            <div key={i} className="flex flex-wrap items-baseline gap-x-4 gap-y-1 rounded-lg border border-zinc-800 bg-zinc-900/50 px-4 py-3"
                 data-testid={`template-caption-${i}`}>
              <span className="font-mono text-xs text-teal-400">{c.start.toFixed(2)}–{c.end.toFixed(2)}s</span>
              <span className="text-sm">{c.text}</span>
              <span className="ml-auto font-mono text-xs text-zinc-500">x {c.position.x}% · y {c.position.y}%</span>
            </div>
          ))}
        </div>

        <Button variant="outline" onClick={() => remove(open.id)} className="mt-12 gap-2 border-red-900/60 text-red-300"
                data-testid="delete-template-btn">
          <Trash2 className="h-4 w-4" /> Borrar plantilla
        </Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0b] px-4 py-10 text-zinc-100 sm:px-6 lg:px-16" data-testid="templates-page">
      <p className="text-xs uppercase tracking-[0.3em] text-teal-400/70">Reelia · plantillas</p>
      <h1 className="mt-3 text-4xl font-semibold sm:text-5xl lg:text-6xl">Reels que ya funcionaron</h1>
      <p className="mt-4 max-w-2xl text-sm text-zinc-400 md:text-base">
        Sube un reel viral y se convierte en plantilla: sus cortes, sus textos y su canción.
      </p>

      <form onSubmit={submit} className="mt-10 max-w-3xl space-y-4 rounded-2xl border border-zinc-800 bg-zinc-900/40 p-6"
            data-testid="template-import-form">
        <input ref={fileRef} type="file" accept="video/*" data-testid="template-file-input"
               className="block w-full text-sm text-zinc-400 file:mr-4 file:rounded-full file:border-0 file:bg-teal-400 file:px-4 file:py-2 file:text-sm file:font-medium file:text-black" />
        <div className="grid gap-4 sm:grid-cols-2">
          {[['name', 'Nombre de la plantilla'], ['source_creator', 'Creador original (sin @)'],
            ['source_music_name', 'Canción'], ['niches', 'Nichos separados por comas']].map(([k, ph]) => (
            <Input key={k} value={form[k]} placeholder={ph} data-testid={`template-input-${k}`}
                   onChange={(e) => setForm((f) => ({ ...f, [k]: e.target.value }))}
                   className="border-zinc-800 bg-zinc-900/80" />
          ))}
        </div>
        <Button type="submit" disabled={uploading} className="gap-2 rounded-full" data-testid="template-import-submit">
          {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
          Importar reel
        </Button>
      </form>

      {error && (
        <div className="mt-6 flex max-w-3xl items-center gap-2 rounded-lg border border-red-900/60 bg-red-950/40 px-4 py-3 text-sm text-red-300"
             data-testid="templates-error">
          <AlertTriangle className="h-4 w-4" /> {error}
        </div>
      )}

      <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4" data-testid="templates-grid">
        {list.length === 0 && <p className="text-sm text-zinc-500" data-testid="templates-empty">Todavía no hay plantillas.</p>}
        {list.map((t) => (
          <button key={t.id} onClick={() => openTemplate(t.id)} data-testid={`template-card-${t.id}`}
                  className="group overflow-hidden rounded-xl border border-zinc-800 bg-zinc-900/50 text-left transition-colors hover:border-teal-500/50">
            <div className="relative aspect-[9/16] bg-black">
              {t.cover_url
                ? <img src={img(t.cover_url)} alt={t.name} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                : <div className="flex h-full items-center justify-center text-xs text-zinc-600">sin fotograma</div>}
              <span className="absolute left-2 top-2"><StatusBadge t={t} /></span>
            </div>
            <div className="p-4">
              <p className="truncate text-sm font-medium">{t.name}</p>
              <p className="mt-1 text-xs text-zinc-500">
                {t.scene_count} escenas · {t.caption_count} captions
                {t.duration ? ` · ${t.duration.toFixed(1)}s` : ''}
              </p>
              {t.caption_warning && (
                <p className="mt-2 flex items-center gap-1.5 text-xs text-amber-300"
                   data-testid={`template-caption-warning-badge-${t.id}`}>
                  <AlertTriangle className="h-3 w-3 shrink-0" /> captions incompletos
                </p>
              )}
              {t.error_message && <p className="mt-2 text-xs text-red-400">{t.error_message}</p>}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}
